import {
  addBusinessDays,
  addDays,
  addMonths,
  isBefore,
  isAfter,
  eachWeekendOfMonth,
  lastDayOfMonth,
  setDate,
  setHours,
  setMinutes,
  setSeconds,
  subDays,
  nextDay,
  previousDay,
  addWeeks,
  eachDayOfInterval,
  differenceInMonths,
  differenceInWeeks,
  differenceInSeconds
} from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { isObject } from './guards';
export { Dayjs, dayjs };

import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.updateLocale('en', {
  weekStart: 1
});
/**
 * methods beginning with "set" | "add" are mutatable
 * methods beginning with "to" | "with" are immutable
 * methods beginning with "get" are immutable - provide getters for the properties
 */

export type DateObjectInputDate = Date | DateObject | Dayjs | number | string | null | undefined;

export type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export class DateObject {
  date: Date;
  constructor(inputDate?: DateObjectInputDate) {
    this.date = inputDate
      ? isObject(inputDate) && 'date' in inputDate
        ? new Date('toDate' in inputDate ? inputDate.toDate() : inputDate.date)
        : new Date(inputDate)
      : new Date();
  }

  clone() {
    return new DateObject(this.date);
  }

  toDayjs(toUtc = false): dayjs.Dayjs {
    if (toUtc) {
      return dayjs(this.date).utc(false);
    }
    return dayjs(this.date);
  }

  toLocalDateModel(): string {
    return this.toDayjs().format('YYYY-MM-DD');
  }

  /**
   * TIME
   */

  setTime(hours: number, minutes: number, seconds?: number): this {
    this.date = this.toTime(hours, minutes, seconds);
    return this;
  }
  toTime(hours: number, minutes: number, seconds?: number): Date {
    const result = setHours(setMinutes(this.date, minutes), hours);
    return typeof seconds === 'number' ? setSeconds(result, seconds) : result;
  }
  setStartOfDay(): this {
    this.setTime(0, 0, 1);
    return this;
  }
  toStartOfDay(): Date {
    return this.toTime(0, 0, 1);
  }

  setEndOfDay(): this {
    this.setTime(23, 59, 59);
    return this;
  }
  toEndOfDay(): Date {
    return this.toTime(23, 59, 59);
  }

  differenceInSeconds(targetDate: DateObjectInputDate, startDate: DateObjectInputDate = this.date): number {
    const leftDate = new DateObject(startDate).date;
    const rightDate = new DateObject(targetDate).date;
    return differenceInSeconds(leftDate, rightDate);
  }

  /**
   * DAYS
   */
  addDays(days: number): this {
    this.date = this.withAddedDays(days);
    return this;
  }
  getDaysInMonth(): number {
    const lastDay = this.clone().setEndOfMonth();
    return lastDay.date.getDate();
  }
  withAddedDays(days: number): Date {
    return addDays(this.date, days);
  }
  setDayOfMonth(dayOfMonth: number): this {
    this.date = this.toDayOfMonth(dayOfMonth);
    return this;
  }
  toDayOfMonth(dayOfMonth: number): Date {
    return setDate(this.date, dayOfMonth);
  }

  setDayOfWeekOfMonth(dayOfWeek: Day, week: number): this {
    this.date = this.toDayOfWeekOfMonth(dayOfWeek, week);
    return this;
  }

  // Sunday = 0
  toDayOfWeekOfMonth(dayOfWeek: Day, week: number): Date {
    const firstDayOfMonth = this.toFirstDayOfMonth();
    const targetDay = firstDayOfMonth.getDay() === dayOfWeek ? firstDayOfMonth : nextDay(firstDayOfMonth, dayOfWeek);
    return addWeeks(targetDay, week - 1);
  }

  setFirstDayOfWeekOfMonth(): this {
    this.setDayOfWeekOfMonth(1, 1);
    return this;
  }
  toFirstDayOfWeekOfMonth(): Date {
    return this.toDayOfWeekOfMonth(1, 1);
  }

  setLastDayOfWeekOfMonth(dayOfWeek: Day): this {
    this.date = this.toLastDayOfWeekOfMonth(dayOfWeek);
    return this;
  }
  toLastDayOfWeekOfMonth(dayOfWeek: Day): Date {
    const lastDay = this.toLastDayOfMonth();
    return lastDay.getDay() === dayOfWeek ? lastDay : previousDay(lastDay, dayOfWeek);
  }

  setFirstDayOfMonth(): this {
    this.date = this.toFirstDayOfMonth();
    return this;
  }
  toFirstDayOfMonth(): Date {
    return this.toDayOfMonth(1);
  }
  setLastDayOfMonth(): this {
    this.date = this.toLastDayOfMonth();
    return this;
  }
  toLastDayOfMonth(): Date {
    return lastDayOfMonth(this.date);
  }

  /**
   * Weekdays
   */
  setWeekdayOfMonth(weekday: number): this {
    this.date = this.toWeekdayOfMonth(weekday);
    return this;
  }
  // weekday - starting with 1
  toWeekdayOfMonth(weekday: number): Date {
    let targetDay: Date = this.toFirstDayOfMonth();
    const dayNumber = targetDay.getDay();

    if (dayNumber === 0) {
      targetDay = addDays(targetDay, 1);
    }
    if (dayNumber === 6) {
      targetDay = addDays(targetDay, 2);
    }
    return addBusinessDays(targetDay, weekday - 1);
  }

  setFirstWeekdayOfMonth(): this {
    this.setWeekdayOfMonth(1);
    return this;
  }
  toFirstWeekdayOfMonth(): Date {
    return this.toWeekdayOfMonth(1);
  }
  setLastWeekdayOfMonth(): this {
    this.date = this.toLastWeekdayOfMonth();
    return this;
  }
  toLastWeekdayOfMonth(): Date {
    const lastDay = this.toLastDayOfMonth();
    const dayNumber = lastDay.getDay();

    // Sunday
    if (dayNumber === 0) {
      return subDays(lastDay, 2);
    }

    // Saturday
    if (dayNumber === 6) {
      return subDays(lastDay, 1);
    }

    return lastDay;
  }

  /**
   * WEEKENDS
   */

  setWeekendingDayOfMonth(day: number): this {
    this.date = this.toWeekendingDayOfMonth(day);
    return this;
  }
  toWeekendingDayOfMonth(day: number): Date {
    const targetDate = this.toWeekendsOfMonth()[day - 1];
    if (!targetDate) {
      // biome-ignore lint/suspicious/noConsole: warning needed
      console.warn(`Weekend day ${day} is not in the month. Defaulting to the current date.`);
      return new Date(this.date);
    }
    return targetDate;
  }

  setFirstWeekendingDayOfMonth(): this {
    this.setWeekendingDayOfMonth(1);
    return this;
  }
  toFirstWeekendingDayOfMonth(): Date {
    return this.toWeekendingDayOfMonth(1);
  }
  setLastWeekendingDayOfMonth(): this {
    const weekends = this.toWeekendsOfMonth();
    const targetDate = weekends[weekends.length - 1];
    this.date = targetDate ?? this.date;
    return this;
  }

  /**
   * WEEKS
   */

  addWeeks(weeks: number): this {
    this.date = this.withAddedWeeks(weeks);
    return this;
  }
  withAddedWeeks(weeks: number): Date {
    return addWeeks(this.date, weeks);
  }

  getNextWeeks(count: number, step = 1): Date[] {
    return Array.from({ length: count }, (_, i) => {
      return this.clone()
        .addWeeks((i + 1) * step)
        .toStartOfDay();
    });
  }

  getWeeksInMonth(): number {
    const lastDay = this.toLastDayOfMonth().getDate();
    return Math.ceil(lastDay / 7);
  }

  differenceInWeeks(targetDate: DateObjectInputDate, startDate: DateObjectInputDate = this.date): number {
    const leftDate = new DateObject(targetDate).date;
    const rightDate = new DateObject(startDate).date;
    return differenceInWeeks(leftDate, rightDate);
  }

  /**
   * MONTHS
   */

  setStartOfMonth(): this {
    this.date = this.toStartOfMonth();
    return this;
  }
  toStartOfMonth(): Date {
    const targetDate = this.toTime(0, 0, 1);
    return setDate(targetDate, 1);
  }
  setEndOfMonth(): this {
    this.date = this.toEndOfMonth();
    return this;
  }
  toEndOfMonth(): Date {
    return new DateObject(lastDayOfMonth(this.date)).toEndOfDay();
  }
  addMonths(months: number): this {
    this.date = this.withAddedMonths(months);
    return this;
  }
  withAddedMonths(months: number): Date {
    return addMonths(this.date, months);
  }

  getMonth() {
    return this.date.getMonth();
  }

  getNextMonths(count: number, step = 1): Date[] {
    return Array.from({ length: count }, (_, i) => {
      return this.clone()
        .addMonths((i + 1) * step)
        .toStartOfMonth();
    });
  }

  differenceInMonths(targetDate: DateObjectInputDate, startDate: DateObjectInputDate = this.date): number {
    const leftDate = new DateObject(targetDate).date;
    const rightDate = new DateObject(startDate).date;
    return differenceInMonths(leftDate, rightDate);
  }

  /**
   * INTERVALS
   */

  toWeekendsOfMonth(): Date[] {
    return eachWeekendOfMonth(this.date);
  }

  toDaysOfMonth(day: Day, startDateObject?: DateObject, endDateObject?: DateObject): Date[] {
    return eachDayOfInterval({
      start: startDateObject?.toStartOfDay() ?? this.toFirstDayOfMonth(),
      end: endDateObject?.toEndOfDay() ?? this.toLastDayOfMonth()
    }).filter((it) => it.getDay() === day);
  }

  /**
   * UTILS
   */
  isBefore(date: Date | number): boolean {
    return isBefore(this.date, date);
  }
  isAfter(date: Date | number): boolean {
    return isAfter(this.date, date);
  }
  isSame(date: number | string | Date | Dayjs | DateObject, unit?: dayjs.OpUnitType | undefined): boolean {
    return dayjs(this.date).isSame(date instanceof DateObject ? date.date : date, unit);
  }
}
